import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, Zap, Award, Sparkles, Heart, Shield, ArrowLeft, Home, User, Palette, Brain } from 'lucide-react';
import { useQuizResults } from '../hooks/useQuizResults';
import { sauceUtils } from '../data/sauces';
import { functionalIngredientUtils } from '../data/functionalIngredients';

interface QuizProps {
  onComplete: (results: any) => void;
  onBackToHero: () => void;
  onBackToWaitlist: () => void;
}

export default function Quiz({ onComplete, onBackToHero, onBackToWaitlist }: QuizProps) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [isCompleting, setIsCompleting] = useState(false);
  const [quizType, setQuizType] = useState<'flavor' | 'persona' | null>(null);
  const { saveQuizResult } = useQuizResults();

  // Quiz type selection questions
  const typeSelectionQuestions = [
    {
      id: 'quiz-type',
      question: 'What type of DrizzID™ profile would you like to create?',
      options: [
        {
          type: 'flavor' as const,
          title: 'Flavor Profile',
          description: 'Discover sauces based on your taste preferences, heat tolerance, and flavor adventure level',
          icon: Palette,
          color: 'from-pink-500 to-purple-600'
        },
        {
          type: 'persona' as const,
          title: 'Sauce Persona',
          description: 'Get recommendations based on your beauty, wellness, and performance goals with functional ingredients',
          icon: User,
          color: 'from-cyan-500 to-blue-600'
        }
      ]
    }
  ];

  // Flavor Profile Questions (Original)
  const flavorQuestions = [
    {
      id: 0,
      category: 'Intensity & Impact',
      question: 'How do you approach new flavors?',
      options: [
        'I dive in headfirst - maximum intensity and bold taste',
        'I prefer gradual exploration with building complexity',
        'I like familiar flavors with interesting twists',
        'I stick to gentle, well-balanced combinations'
      ]
    },
    {
      id: 1,
      category: 'Heat & Spice',
      question: 'Your relationship with spice:',
      options: [
        'Molten lava - bring the burn!',
        'Hot enough to make me sweat',
        'Gentle warmth that builds',
        'Mild heat or no heat at all'
      ]
    },
    {
      id: 2,
      category: 'Flavor Complexity',
      question: 'Which flavor combination excites you most?',
      options: [
        'Sweet heat with smoky undertones',
        'Tangy citrus with herbal notes',
        'Rich umami with earthy depth',
        'Classic combinations I know and love'
      ]
    },
    {
      id: 3,
      category: 'Adventure Level',
      question: 'Your ideal sauce discovery journey:',
      options: [
        'Exotic ingredients from around the world',
        'Regional specialties with unique twists',
        'Familiar flavors with premium quality',
        'Time-tested classics done perfectly'
      ]
    },
    {
      id: 4,
      category: 'Pairing Style',
      question: 'How do you use sauces?',
      options: [
        'Transform every meal into an adventure',
        'Enhance specific dishes strategically',
        'Add consistent flavor to daily meals',
        'Complement food without overpowering'
      ]
    },
    {
      id: 5,
      category: 'Flavor Philosophy',
      question: 'What drives your food choices?',
      options: [
        'Bold experiences and memorable moments',
        'Quality ingredients and craftsmanship',
        'Comfort and reliable satisfaction',
        'Health and natural goodness'
      ]
    }
  ];

  // Sauce Persona Questions (New)
  const personaQuestions = [
    {
      id: 0,
      category: 'Beauty Goals',
      question: 'What are your primary beauty and anti-aging goals?',
      options: [
        'Radiant skin, anti-aging, and natural glow enhancement',
        'Hair strength, nail health, and overall beauty support',
        'Hydration, elasticity, and youthful appearance',
        'General beauty maintenance and protection'
      ]
    },
    {
      id: 1,
      category: 'Wellness Priorities',
      question: 'Which wellness area is most important to you?',
      options: [
        'Stress management and mental balance',
        'Digestive health and gut wellness',
        'Immune support and overall vitality',
        'Sleep quality and recovery'
      ]
    },
    {
      id: 2,
      category: 'Performance Focus',
      question: 'What type of performance enhancement interests you most?',
      options: [
        'Physical energy and athletic performance',
        'Mental clarity and cognitive function',
        'Endurance and recovery optimization',
        'General daily energy and focus'
      ]
    },
    {
      id: 3,
      category: 'Lifestyle Integration',
      question: 'How do you prefer to integrate wellness into your routine?',
      options: [
        'High-impact, targeted interventions',
        'Consistent daily support throughout meals',
        'Convenient, on-the-go solutions',
        'Gentle, natural wellness maintenance'
      ]
    },
    {
      id: 4,
      category: 'Ingredient Philosophy',
      question: 'What\'s your approach to functional ingredients?',
      options: [
        'Cutting-edge, scientifically-backed compounds',
        'Traditional herbs with modern research',
        'Natural superfoods and adaptogens',
        'Simple, proven vitamins and minerals'
      ]
    },
    {
      id: 5,
      category: 'Wellness Goals',
      question: 'Which combination of benefits appeals to you most?',
      options: [
        'Beauty + Performance: Glow while you go',
        'Wellness + Beauty: Inner health, outer radiance',
        'Performance + Wellness: Strong body, calm mind',
        'Balanced approach: A little of everything'
      ]
    }
  ];

  const getCurrentQuestions = () => {
    if (quizType === 'flavor') return flavorQuestions;
    if (quizType === 'persona') return personaQuestions;
    return [];
  };

  const handleQuizTypeSelection = (selectedType: 'flavor' | 'persona') => {
    setQuizType(selectedType);
    setCurrentQuestion(0);
    setAnswers({});
  };

  const handleAnswer = (answerIndex: number) => {
    setAnswers({ ...answers, [currentQuestion]: answerIndex });
  };

  const nextQuestion = () => {
    const questions = getCurrentQuestions();
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      completeQuiz();
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    } else if (quizType) {
      // Go back to quiz type selection
      setQuizType(null);
      setCurrentQuestion(0);
      setAnswers({});
    }
  };

  const completeQuiz = async () => {
    setIsCompleting(true);
    
    try {
      // Generate profile data based on quiz type
      const sauceProfile = quizType === 'flavor' 
        ? generateFlavorProfile(answers)
        : generatePersonaProfile(answers);
      
      // Try to save to Supabase (optional - no login required)
      let savedResult = null;
      try {
        savedResult = await saveQuizResult(sauceProfile, answers);
      } catch (error) {
        console.log('Quiz saved locally (no database connection)');
      }
      
      // Complete the quiz with enhanced results
      const results = {
        answers,
        sauceProfile: {
          ...sauceProfile,
          quizType,
          quizResultId: savedResult?.id,
          shareToken: savedResult?.share_token,
          savedResult
        },
        timestamp: new Date().toISOString()
      };
      
      setTimeout(() => {
        onComplete(results);
      }, 1000);
      
    } catch (error) {
      console.error('Error completing quiz:', error);
      
      // Still complete the quiz even if saving fails
      const sauceProfile = quizType === 'flavor' 
        ? generateFlavorProfile(answers)
        : generatePersonaProfile(answers);
      
      const results = {
        answers,
        sauceProfile: { ...sauceProfile, quizType },
        timestamp: new Date().toISOString()
      };
      
      setTimeout(() => {
        onComplete(results);
      }, 1000);
    }
  };

  const generateFlavorProfile = (answers: Record<number, number>) => {
    const intensityScore = (answers[0] || 0) * 25;
    const heatLevel = (answers[1] || 0) * 25;
    const complexity = (answers[2] || 0) * 25;
    const adventureScore = (answers[3] || 0) * 25;
    const pairingStyle = answers[4] || 0;
    const philosophy = answers[5] || 0;
    
    // Generate sauce recommendations
    const allSauces = sauceUtils.getAllSauces();
    const sauceRecommendations = allSauces.map(sauce => {
      let compatibility = 50; // Base compatibility
      
      // Adjust based on heat preference
      if (sauce.heat === 'Extreme' && heatLevel >= 75) compatibility += 30;
      else if (sauce.heat === 'Hot' && heatLevel >= 50) compatibility += 25;
      else if (sauce.heat === 'Medium' && heatLevel >= 25) compatibility += 20;
      else if (sauce.heat === 'Mild' && heatLevel < 25) compatibility += 25;
      
      // Adjust based on adventure level
      if (adventureScore >= 75) compatibility += 15;
      else if (adventureScore < 25) compatibility -= 10;
      
      // Adjust based on intensity
      if (intensityScore >= 75) compatibility += 10;
      
      compatibility = Math.min(100, Math.max(0, compatibility));
      
      return {
        ...sauce,
        compatibility,
        reasons: generateFlavorReasons(sauce, { intensityScore, heatLevel, complexity, adventureScore })
      };
    }).sort((a, b) => b.compatibility - a.compatibility);

    return {
      intensity: intensityScore,
      heat: heatLevel,
      complexity: complexity,
      adventure: adventureScore,
      profileType: getFlavorProfileType(intensityScore, heatLevel, complexity, adventureScore),
      flavorPersonality: getFlavorPersonality(philosophy),
      pairingStyle: getPairingStyle(pairingStyle),
      primaryCharacteristics: getFlavorCharacteristics(answers),
      preferences: {
        heatLevel: getHeatLabel(heatLevel),
        complexityLevel: getComplexityLabel(complexity),
        adventureLevel: getAdventureLabel(adventureScore)
      },
      strengths: getFlavorStrengths(answers),
      topSauceMatch: sauceRecommendations[0],
      sauceRecommendations
    };
  };

  const generatePersonaProfile = (answers: Record<number, number>) => {
    const beautyScore = (answers[0] || 0) * 25;
    const wellnessScore = (answers[1] || 0) * 25;
    const performanceScore = (answers[2] || 0) * 25;
    const lifestyleIntegration = answers[3] || 0;
    const ingredientPhilosophy = answers[4] || 0;
    const goalCombination = answers[5] || 0;
    
    // Generate sauce recommendations based on persona
    const allSauces = sauceUtils.getAllSauces();
    const sauceRecommendations = allSauces.map(sauce => {
      let compatibility = 60; // Base compatibility for persona quiz
      
      // Adjust based on primary goals
      if (goalCombination === 0) compatibility += beautyScore * 0.3; // Beauty + Performance
      else if (goalCombination === 1) compatibility += (beautyScore + wellnessScore) * 0.2; // Wellness + Beauty
      else if (goalCombination === 2) compatibility += (performanceScore + wellnessScore) * 0.2; // Performance + Wellness
      else compatibility += (beautyScore + wellnessScore + performanceScore) * 0.1; // Balanced
      
      compatibility = Math.min(100, Math.max(0, compatibility));
      
      return {
        ...sauce,
        compatibility,
        reasons: generatePersonaReasons(sauce, { beautyScore, wellnessScore, performanceScore, goalCombination })
      };
    }).sort((a, b) => b.compatibility - a.compatibility);

    return {
      beautyScore,
      wellnessScore,
      performanceScore,
      balance: Math.round((beautyScore + wellnessScore + performanceScore) / 3),
      profileType: getPersonaProfileType(beautyScore, wellnessScore, performanceScore),
      personaType: getPersonaType(goalCombination),
      primaryGoals: getPrimaryGoals(answers),
      wellnessPhilosophy: getWellnessPhilosophy(ingredientPhilosophy),
      lifestyleType: getLifestyleType(lifestyleIntegration),
      topSauceMatch: sauceRecommendations[0],
      sauceRecommendations
    };
  };

  // Helper functions for flavor profile
  const getFlavorProfileType = (intensity: number, heat: number, complexity: number, adventure: number) => {
    const total = intensity + heat + complexity + adventure;
    if (total > 300) return 'Fire Flavor Alchemist';
    if (total > 200) return 'Bold Taste Explorer';
    if (total > 100) return 'Flavor Curator';
    return 'Classic Connoisseur';
  };

  const getFlavorPersonality = (philosophy: number) => {
    const personalities = ['Adventure Seeker', 'Quality Focused', 'Comfort Lover', 'Health Conscious'];
    return personalities[philosophy] || 'Balanced Explorer';
  };

  const getPairingStyle = (style: number) => {
    const styles = [
      'Transformative - turns every meal into an adventure',
      'Strategic - enhances specific dishes with precision',
      'Consistent - adds reliable flavor to daily meals',
      'Complementary - supports food without overpowering'
    ];
    return styles[style] || 'Versatile approach';
  };

  const getFlavorCharacteristics = (answers: Record<number, number>) => {
    const characteristics = [];
    if ((answers[0] || 0) >= 2) characteristics.push('Bold');
    if ((answers[1] || 0) >= 2) characteristics.push('Heat Lover');
    if ((answers[2] || 0) >= 2) characteristics.push('Complex Palate');
    if ((answers[3] || 0) >= 2) characteristics.push('Adventurous');
    return characteristics.length > 0 ? characteristics : ['Balanced', 'Thoughtful'];
  };

  const getFlavorStrengths = (answers: Record<number, number>) => {
    return [
      { name: 'Intensity', score: (answers[0] || 0) * 25 },
      { name: 'Heat Tolerance', score: (answers[1] || 0) * 25 },
      { name: 'Complexity Appreciation', score: (answers[2] || 0) * 25 },
      { name: 'Adventure Spirit', score: (answers[3] || 0) * 25 }
    ].sort((a, b) => b.score - a.score);
  };

  // Helper functions for persona profile
  const getPersonaProfileType = (beauty: number, wellness: number, performance: number) => {
    const highest = Math.max(beauty, wellness, performance);
    if (highest === beauty) return 'Beauty-Focused Wellness Enthusiast';
    if (highest === wellness) return 'Holistic Wellness Advocate';
    if (highest === performance) return 'Performance-Driven Optimizer';
    return 'Balanced Lifestyle Curator';
  };

  const getPersonaType = (combination: number) => {
    const types = ['Beauty Performer', 'Wellness Beauty', 'Performance Wellness', 'Balanced Lifestyle'];
    return types[combination] || 'Holistic Approach';
  };

  const getPrimaryGoals = (answers: Record<number, number>) => {
    const goals = [];
    if ((answers[0] || 0) >= 2) goals.push('Beauty Enhancement');
    if ((answers[1] || 0) >= 2) goals.push('Wellness Support');
    if ((answers[2] || 0) >= 2) goals.push('Performance Optimization');
    return goals.length > 0 ? goals : ['Balanced Wellness'];
  };

  const getWellnessPhilosophy = (philosophy: number) => {
    const philosophies = [
      'Cutting-edge science meets optimal results',
      'Traditional wisdom enhanced by modern research',
      'Natural superfoods for sustainable wellness',
      'Simple, proven nutrients for daily health'
    ];
    return philosophies[philosophy] || 'Balanced approach to wellness';
  };

  const getLifestyleType = (lifestyle: number) => {
    const types = ['High-Impact', 'Daily Support', 'Convenient', 'Gentle Maintenance'];
    return types[lifestyle] || 'Flexible';
  };

  // Reason generators
  const generateFlavorReasons = (sauce: any, profile: any) => {
    const reasons = [];
    if (sauce.heat === 'Extreme' && profile.heatLevel >= 75) {
      reasons.push('Matches your extreme heat preference');
    }
    if (profile.adventureScore >= 75) {
      reasons.push('Perfect for your adventurous palate');
    }
    if (profile.intensityScore >= 75) {
      reasons.push('Bold flavor matches your intensity level');
    }
    return reasons.slice(0, 2);
  };

  const generatePersonaReasons = (sauce: any, profile: any) => {
    const reasons = [];
    if (profile.goalCombination === 0) {
      reasons.push('Supports beauty and performance goals');
    }
    if (profile.beautyScore >= 75) {
      reasons.push('Ideal for beauty enhancement');
    }
    if (profile.wellnessScore >= 75) {
      reasons.push('Supports overall wellness');
    }
    return reasons.slice(0, 2);
  };

  const getHeatLabel = (score: number) => {
    if (score >= 75) return 'Fire Lover';
    if (score >= 50) return 'Heat Seeker';
    if (score >= 25) return 'Mild Heat';
    return 'Cool & Calm';
  };

  const getComplexityLabel = (score: number) => {
    if (score >= 75) return 'Complex Connoisseur';
    if (score >= 50) return 'Flavor Explorer';
    if (score >= 25) return 'Balanced Palate';
    return 'Simple & Clean';
  };

  const getAdventureLabel = (score: number) => {
    if (score >= 75) return 'Fearless Explorer';
    if (score >= 50) return 'Curious Taster';
    if (score >= 25) return 'Cautious Explorer';
    return 'Traditional Tastes';
  };

  if (isCompleting) {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden px-4">
        {/* Enhanced AI Processing Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-pink-500/20 via-purple-600/30 to-cyan-600/20 animate-pulse"></div>
        
        {/* AI Neural Network Animation */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-cyan-400 rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${1 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>
        
        <div className="relative z-10 text-center max-w-lg mx-auto">
          <div className="relative mb-6 sm:mb-8">
            <div className="w-24 h-24 sm:w-32 sm:h-32 border-4 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin mx-auto"></div>
            <Sparkles className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-8 w-8 sm:h-12 sm:w-12 text-cyan-400 animate-pulse" />
          </div>
          
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">
            Analyzing Your Enhanced DrizzID™...
          </h2>
          <p className="text-gray-400 mb-4 sm:mb-6 text-sm sm:text-base">
            Our AI is creating your {quizType === 'persona' ? 'wellness-focused' : 'flavor-focused'} profile and recommendations
          </p>
          
          <div className="space-y-3 sm:space-y-4">
            {quizType === 'persona' ? (
              <>
                <div className="flex items-center justify-center space-x-2 sm:space-x-3 text-pink-300 text-sm sm:text-base">
                  <Heart className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Processing beauty enhancement markers...</span>
                </div>
                <div className="flex items-center justify-center space-x-2 sm:space-x-3 text-green-300 text-sm sm:text-base">
                  <Shield className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Analyzing wellness compatibility...</span>
                </div>
                <div className="flex items-center justify-center space-x-2 sm:space-x-3 text-blue-300 text-sm sm:text-base">
                  <Zap className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Optimizing performance ingredients...</span>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-center space-x-2 sm:space-x-3 text-pink-300 text-sm sm:text-base">
                  <Palette className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Mapping flavor preferences...</span>
                </div>
                <div className="flex items-center justify-center space-x-2 sm:space-x-3 text-orange-300 text-sm sm:text-base">
                  <Zap className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Analyzing heat tolerance...</span>
                </div>
                <div className="flex items-center justify-center space-x-2 sm:space-x-3 text-purple-300 text-sm sm:text-base">
                  <Award className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Calculating sauce compatibility...</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Quiz Type Selection Screen
  if (!quizType) {
    return (
      <div className="min-h-screen py-16 sm:py-20 relative overflow-hidden">
        {/* Enhanced Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-pink-500/20 via-purple-600/30 to-cyan-600/20 animate-pulse"></div>

        {/* Header with Navigation */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-pink-500/20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16 sm:h-20">
              <div className="flex items-center space-x-2 sm:space-x-4">
                <img 
                  src="/logo transparent with tagline.png" 
                  alt="UNDRESSING Logo" 
                  className="h-12 sm:h-16 w-auto object-contain cursor-pointer transition-all duration-300 hover:scale-105 hover:opacity-80"
                  onClick={onBackToWaitlist}
                  title="Back to UNDRESSING Homepage"
                />
              </div>

              <div className="flex items-center space-x-2 sm:space-x-4">
                <button
                  onClick={onBackToHero}
                  className="hidden sm:flex items-center space-x-2 px-3 py-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
                >
                  <ArrowLeft className="h-4 w-4" />
                  <span>Hero</span>
                </button>
                <button
                  onClick={onBackToWaitlist}
                  className="flex items-center space-x-2 px-3 py-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
                >
                  <Home className="h-4 w-4" />
                  <span className="hidden sm:inline">Home</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center space-x-2 sm:space-x-3 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 border border-cyan-500/30 rounded-full px-4 sm:px-6 py-2 sm:py-3 mb-6 sm:mb-8">
              <Brain className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-400" />
              <span className="text-cyan-300 font-medium text-sm sm:text-base">AI-Powered DrizzID™ Analysis</span>
            </div>
            
            <h1 className="font-black text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-tight text-white mb-6 sm:mb-8">
              Choose Your
              <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent block">
                DrizzID™ Path
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl lg:text-2xl text-slate-300 leading-relaxed max-w-3xl mx-auto">
              Select the type of personalized recommendations you'd like to receive
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {typeSelectionQuestions[0].options.map((option, index) => {
              const Icon = option.icon;
              return (
                <button
                  key={option.type}
                  onClick={() => handleQuizTypeSelection(option.type)}
                  className="group bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-sm border border-cyan-500/20 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 hover:border-cyan-400/40 transition-all duration-300 hover:scale-105 text-left"
                >
                  <div className={`w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-r ${option.color} rounded-2xl flex items-center justify-center mb-6 sm:mb-8 group-hover:scale-110 transition-transform duration-300`}>
                    <Icon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-white" />
                  </div>
                  
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6 group-hover:text-cyan-400  transition-colors">
                    {option.title}
                  </h3>
                  
                  <p className="text-gray-300 text-base sm:text-lg lg:text-xl leading-relaxed mb-6 sm:mb-8">
                    {option.description}
                  </p>
                  
                  <div className="flex items-center space-x-3 text-cyan-400 group-hover:text-cyan-300 transition-colors">
                    <span className="font-semibold">Start {option.title}</span>
                    <ChevronRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  const questions = getCurrentQuestions();
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="min-h-screen py-16 sm:py-20 relative overflow-hidden">
      {/* Enhanced Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-pink-500/20 via-purple-600/30 to-cyan-600/20 animate-pulse"></div>
      
      {/* Floating AI elements */}
      <div className="absolute top-20 left-10 w-2 h-2 sm:w-3 sm:h-3 bg-cyan-500 rounded-full animate-pulse"></div>
      <div className="absolute top-40 right-20 w-3 h-3 sm:w-4 sm:h-4 bg-purple-500 rounded-full animate-bounce"></div>
      <div className="absolute bottom-40 left-20 w-1 h-1 sm:w-2 sm:h-2 bg-pink-400 rounded-full animate-ping"></div>

      {/* Header with Navigation */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-md border-b border-pink-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <img 
                src="/logo transparent with tagline.png" 
                alt="UNDRESSING Logo" 
                className="h-12 sm:h-16 w-auto object-contain cursor-pointer transition-all duration-300 hover:scale-105 hover:opacity-80"
                onClick={onBackToWaitlist}
                title="Back to UNDRESSING Homepage"
              />
            </div>

            <div className="flex items-center space-x-4">
              <span className="text-gray-400 text-sm sm:text-base">Question {currentQuestion + 1} of {questions.length}</span>
              <div className="w-20 sm:w-32 bg-gray-800 rounded-full h-2">
                <div 
                  className="bg-gradient-to-r from-cyan-500 to-purple-600 h-2 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={onBackToHero}
                className="hidden sm:flex items-center space-x-2 px-3 py-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
              >
                <ArrowLeft className="h-4 w-4" />
                <span>Hero</span>
              </button>
              <button
                onClick={onBackToWaitlist}
                className="flex items-center space-x-2 px-3 py-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-white/10"
              >
                <Home className="h-4 w-4" />
                <span className="hidden sm:inline">Home</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 pt-24 sm:pt-28">
        {/* Enhanced Progress Bar */}
        <div className="mb-6 sm:mb-8">
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            <div className="flex items-center space-x-2 sm:space-x-3">
              {quizType === 'persona' ? (
                <>
                  <User className="h-4 w-4 sm:h-5 sm:w-5 text-cyan-400" />
                  <span className="text-cyan-400 font-semibold text-sm sm:text-base">Sauce Persona Analysis</span>
                </>
              ) : (
                <>
                  <Palette className="h-4 w-4 sm:h-5 sm:w-5 text-pink-400" />
                  <span className="text-pink-400 font-semibold text-sm sm:text-base">Flavor Profile Analysis</span>
                </>
              )}
            </div>
            <span className="text-gray-400 text-sm sm:text-base">{Math.round(progress)}% Complete</span>
          </div>
          <div className="w-full bg-gray-800 rounded-full h-2 sm:h-3">
            <div 
              className="bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-600 h-2 sm:h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Enhanced Question Card */}
        <div className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-sm border border-cyan-500/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8">
          <div className="mb-4 sm:mb-6">
            <div className="flex items-center space-x-2 sm:space-x-3 mb-3 sm:mb-4">
              <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-xs sm:text-sm">{currentQuestion + 1}</span>
              </div>
              <span className="text-cyan-400 text-xs sm:text-sm font-semibold uppercase tracking-wide">
                {questions[currentQuestion].category}
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mt-2 mb-4 sm:mb-6">
              {questions[currentQuestion].question}
            </h2>
          </div>

          <div className="space-y-3 sm:space-y-4">
            {questions[currentQuestion].options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(index)}
                className={`w-full text-left p-3 sm:p-4 rounded-lg sm:rounded-xl transition-all duration-300 border ${
                  answers[currentQuestion] === index
                    ? 'bg-gradient-to-r from-cyan-500/20 to-purple-600/20 border-cyan-500 text-white'
                    : 'bg-white/5 border-gray-700 text-gray-300 hover:bg-white/10 hover:border-gray-600'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full border-2 mt-1 flex-shrink-0 ${
                    answers[currentQuestion] === index
                      ? 'bg-cyan-500 border-cyan-500'
                      : 'border-gray-500'
                  }`}></div>
                  <span className="text-sm sm:text-base">{option}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Enhanced Navigation */}
        <div className="flex justify-between items-center">
          <button
            onClick={prevQuestion}
            className="flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 text-gray-400 hover:text-white transition-colors text-sm sm:text-base"
          >
            <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
            <span>Previous</span>
          </button>

          <button
            onClick={nextQuestion}
            disabled={answers[currentQuestion] === undefined}
            className="flex items-center space-x-2 bg-gradient-to-r from-cyan-500 to-purple-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full font-semibold hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          >
            <span>{currentQuestion === questions.length - 1 ? 'Generate DrizzID™' : 'Next'}</span>
            {currentQuestion === questions.length - 1 ? (
              <Award className="h-4 w-4 sm:h-5 sm:w-5" />
            ) : (
              <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}